import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Header from '../Header';
import './SignUp.css';

const SignUp = () => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [sex, setSex] = useState('');
    const [birthdate, setBirthdate] = useState('');
    const [email, setEmail] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState({
        minLength: false,
        hasNumberOrSymbol: false,
        notNameOrEmail: true,
    });
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const [existingPatientError, setExistingPatientError] = useState('');
    const [validationTimeout, setValidationTimeout] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        const signupContainer = document.querySelector('.signup-container');
        signupContainer.classList.add('active');
    }, []);

    useEffect(() => {
        validatePassword(password);
    }, [password, firstName, lastName, email]);

    useEffect(() => {
        // Clear validation when fields change
        setExistingPatientError('');
    }, [firstName, lastName, email, contactNumber]);

    const validatePassword = (password) => {
        const minLength = password.length >= 8;
        const hasNumberOrSymbol = /[0-9!@#$%^&*]/.test(password);
        const notNameOrEmail = !password.includes(firstName) && !password.includes(lastName) && !password.includes(email);

        setPasswordValidation({
            minLength,
            hasNumberOrSymbol,
            notNameOrEmail,
        });

        if (minLength && hasNumberOrSymbol && notNameOrEmail) {
            setIsPasswordValid(true);
        } else {
            setIsPasswordValid(false);
        }
    };

    const checkExistingPatient = async () => {
        if (!firstName || !lastName || (!email && !contactNumber && !birthdate)) return true;
    
        try {
            const response = await fetch('https://api.hgcdental.clinic/api/patients');
            if (!response.ok) {
                throw new Error('Failed to fetch patient data');
            }
            const patients = await response.json();
    
            // Normalize input data
            const inputFirstName = firstName.toLowerCase();
            const inputLastName = lastName.toLowerCase();
            const inputBirthdate = birthdate ? new Date(birthdate).toISOString().split('T')[0] : null;
            const normalizedContact = contactNumber ? contactNumber.replace(/\D/g, '') : null;
    
            // Check for matching records
            const matchingPatients = patients.filter(patient => {
                const [patientFirstName, patientLastName] = patient.name.toLowerCase().split(' ');
                const patientBirthdate = patient.birthdate ? 
                    new Date(patient.birthdate).toISOString().split('T')[0] : null;
                const patientContact = patient.ContactInformation ? 
                    patient.ContactInformation.replace(/\D/g, '') : null;
    
                // Name match (first AND last name)
                const nameMatch = (
                    (patientFirstName === inputFirstName && patientLastName === inputLastName) ||
                    patient.name.toLowerCase().includes(firstName.toLowerCase()) ||
                    patient.name.toLowerCase().includes(lastName.toLowerCase())
                );
    
                // Email match
                const emailMatch = email && patient.email === email;
    
                // Contact match
                const contactMatch = normalizedContact && patientContact && 
                    patientContact.includes(normalizedContact);
    
                // Strict name + birthdate match
                const strictNameBirthdateMatch = (
                    inputBirthdate && 
                    patientBirthdate && 
                    patientBirthdate === inputBirthdate &&
                    patientFirstName === inputFirstName &&
                    patientLastName === inputLastName
                );
    
                // Return true if any of these matches exist
                // (but strictNameBirthdateMatch is now required for name+birthdate case)
                return strictNameBirthdateMatch || emailMatch || contactMatch || nameMatch;
            });
    
            if (matchingPatients.length > 0) {
                let errorMessage = 'A patient with matching ';
                const errors = [];
                
                matchingPatients.forEach(patient => {
                    const [patientFirstName, patientLastName] = patient.name.toLowerCase().split(' ');
                    const patientBirthdate = patient.birthdate ? 
                        new Date(patient.birthdate).toISOString().split('T')[0] : null;
                    
                    // Check for strict name + birthdate match
                    if (inputBirthdate && patientBirthdate === inputBirthdate &&
                        patientFirstName === inputFirstName &&
                        patientLastName === inputLastName && 
                        !errors.includes('name and birthdate')) {
                        errors.push('name and birthdate');
                    }
                    
                    // Check other matches
                    if (patient.name.toLowerCase() === `${inputFirstName} ${inputLastName}` && !errors.includes('name')) {
                        errors.push('name');
                    }
                    if (email && patient.email === email && !errors.includes('email')) {
                        errors.push('email');
                    }
                    if (normalizedContact && patient.ContactInformation && 
                        patient.ContactInformation.replace(/\D/g, '').includes(normalizedContact) && 
                        !errors.includes('contact number')) {
                        errors.push('contact number');
                    }
                });
                
                if (errors.length > 0) {
                    errorMessage += errors.join(', ') + ' already exists in our records.';
                    
                    // Special cases
                    if (errors.includes('name and birthdate')) {
                        errorMessage += ' This appears to be an existing patient. Please contact support if you believe this is an error.';
                    } else if (errors.includes('email')) {
                        errorMessage += ' Try using a different email address or recovering your account.';
                    }
                    
                    setExistingPatientError(errorMessage);
                    return false;
                }
            }
            
            return true;
        } catch (error) {
            console.error('Error checking patient records:', error);
            setMessage('Error checking patient records. Please try again.');
            return false;
        }
    };
    
    const generateInitials = (firstName, lastName) => {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    const handleSignUp = async (event) => {
        event.preventDefault();
        setMessage('');
        setLoading(true);

        if (!termsAccepted) {
            setMessage('You must accept the Privacy Terms and Conditions.');
            setLoading(false);
            return;
        }

        if (!firstName || !lastName || !sex || !birthdate || !email || !contactNumber || !password) {
            setMessage('All fields are required.');
            setLoading(false);
            return;
        }

        if (!isPasswordValid) {
            setMessage('Password does not meet the requirements.');
            setLoading(false);
            return;
        }

        // Check for existing patient
        const isPatientUnique = await checkExistingPatient();
        if (!isPatientUnique) {
            setLoading(false);
            return;
        }

        const initials = generateInitials(firstName, lastName);
        const requestBody = { 
            firstName, 
            lastName, 
            sex,
            birthdate,
            email, 
            contactNumber, 
            password,
            initials
        };

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem("email", email);
                localStorage.setItem("password", password);
                setMessage('Account created! Check your email for the verification link.');
            } else {
                setMessage(data.message || 'Registration failed.');
            }
        } catch (error) {
            console.error('Error during registration:', error);
            setMessage('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Debounced validation for existing patients
    const handleFieldChange = (field, value) => {
        // Clear any previous timeout
        if (validationTimeout) {
            clearTimeout(validationTimeout);
        }

        // Update the field value
        switch (field) {
            case 'firstName':
                setFirstName(value);
                break;
            case 'lastName':
                setLastName(value);
                break;
            case 'email':
                setEmail(value);
                break;
            case 'contactNumber':
                setContactNumber(value);
                break;
            default:
                break;
        }

        // Set a new timeout to check for existing patients
        if (value && (field === 'email' || field === 'contactNumber' || 
                     (field === 'firstName' && lastName) || 
                     (field === 'lastName' && firstName))) {
            setValidationTimeout(setTimeout(() => {
                checkExistingPatient();
            }, 1000));
        }
    };

    return (
        <div className="signup-container">
            <Header />
            <div className="signup-content">
                <div className="signup-card">
                    <h1 className="signup-title">Sign Up</h1>
                    <p className="signup-description">Ready to join? Fill out the form to get started.</p>
    
                    <form onSubmit={handleSignUp} className="signup-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="firstName">First Name</label>
                                <div className="input-container">
                                    <input
                                        type="text"
                                        id="firstName"
                                        value={firstName}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^[A-Za-z\s]*$/.test(value)) {
                                                handleFieldChange('firstName', value);
                                            }
                                        }}
                                        placeholder="Enter your first name"
                                        className="modal-input"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="lastName">Last Name</label>
                                <div className="input-container">
                                    <input
                                        type="text"
                                        id="lastName"
                                        value={lastName}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^[A-Za-z\s]*$/.test(value)) {
                                                handleFieldChange('lastName', value);
                                            }
                                        }}
                                        placeholder="Enter your last name"
                                        className="modal-input"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                        {existingPatientError && <p className="error-message">{existingPatientError}</p>}
    
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="sex">Sex</label>
                                <div className="input-container">
                                    <select
                                        id="sex"
                                        value={sex}
                                        onChange={(e) => setSex(e.target.value)}
                                        className="modal-input"
                                        required
                                    >
                                        <option value="">Select your sex</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="birthdate">Birthdate</label>
                                <div className="input-container">
                                    <input
                                        type="date"
                                        id="birthdate"
                                        value={birthdate}
                                        onChange={(e) => setBirthdate(e.target.value)}
                                        className="modal-input"
                                        required
                                        max={new Date().toISOString().split('T')[0]}
                                    />
                                </div>
                            </div>
                        </div>
    
                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            <div className="input-container">
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => handleFieldChange('email', e.target.value)}
                                    placeholder="Enter your email"
                                    className="modal-input"
                                    required
                                />
                            </div>
                        </div>
    
                        <div className="form-group">
                            <label htmlFor="contactNumber">Mobile Number</label>
                            <div className="input-container">
                                <input
                                    type="tel"
                                    id="contactNumber"
                                    value={contactNumber}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^[0-9]*$/.test(value)) {
                                            handleFieldChange('contactNumber', value);
                                        }
                                    }}
                                    placeholder="Enter your mobile number"
                                    className="modal-input"
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <div className="password-container">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onFocus={() => setShowValidation(true)}
                                    onBlur={() => setTimeout(() => setShowValidation(false), 200)}
                                    placeholder="Enter your password"
                                    className="password-input"
                                    required
                                />
                                <span className="signup-eye-icon" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </span>
                            </div>
                            <div className="password-validation" style={{ display: showValidation ? 'block' : 'none' }}>
                                <ul>
                                    <li className={passwordValidation.minLength ? 'valid' : 'invalid'}>
                                        At least 8 characters
                                    </li>
                                    <li className={passwordValidation.hasNumberOrSymbol ? 'valid' : 'invalid'}>
                                        Contains a number or symbol
                                    </li>
                                    <li className={passwordValidation.notNameOrEmail ? 'valid' : 'invalid'}>
                                        Cannot contain your name or email address
                                    </li>
                                </ul>
                            </div>
                        </div>
    
                        <div className="terms-container">
                            <input
                                type="checkbox"
                                id="terms"
                                className="terms-checkbox"
                                checked={termsAccepted}
                                onChange={() => setTermsAccepted(!termsAccepted)}
                            />
                            <label htmlFor="terms" className="terms-text">
                                I accept the
                                <span className="terms-link" onClick={() => setShowTermsModal(true)}>
                                    Privacy Terms and Conditions
                                </span>.
                            </label>
                        </div>
                        <button 
                            type="submit" 
                            className="signup-button" 
                            disabled={loading || !isPasswordValid || !termsAccepted || existingPatientError}
                        >
                            {loading ? 'Creating Account...' : 'Create Account'}
                        </button>
                        <p className="signup-footer">
                        Already have an account? <Link to="/login">Login here</Link>
                    </p>
                    </form>
                    {message && <p className="signup-message">{message}</p>}
                </div>
            </div>
    
            {showTermsModal && (
                <div className="signup-modal-overlay">
                    <div className="signup-modal">
                        <h2>Privacy Policy & Terms and Conditions</h2>
                        <p>Welcome to HGC Dental Clinic. Your privacy is important to us.</p>
                        <p><strong>Information We Collect:</strong> We collect name, contact details, and medical history.</p>
                        <p><strong>How We Use Your Information:</strong> We use data to schedule appointments, maintain records, and improve services.</p>
                        <p><strong>Data Protection & Security:</strong> We implement strict measures to safeguard your data.</p>
                        <p><strong>Sharing of Information:</strong> Data is not shared except when required by law.</p>
                        <p><strong>Your Rights:</strong> You can access, correct, or delete your data where applicable.</p>
                        <p><strong>Use of Services:</strong> By booking an appointment, you agree to our policies.</p>
                        <p><strong>Medical Disclaimer:</strong> Information provided is not a substitute for professional advice.</p>
                        <p><strong>Changes to Policies:</strong> We may update our policies, and changes will be posted.</p>
                        <p>By signing up, you acknowledge that you have read and agreed to these terms.</p>
                        <button onClick={() => setShowTermsModal(false)}>Close</button>
                    </div>            
                </div>
            )}
        </div>
    );
}
    
export default SignUp;
//hi